import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useWindowSize } from '@app/hooks';
import { useLeadsRegistrySettings } from '@app/pages/Leads/LeadsRegistryPage/hooks/useLeadsRegistrySettings';

import type { LeadsFunnelBoardStage } from '../../../../model/types';
import type { RefCallback, UIEvent } from 'react';

export const MIN_LEADS_FUNNEL_STAGE_COLUMN_WIDTH = 240;
export const LEADS_FUNNEL_STAGE_COLUMN_GAP = 8;

const LEADS_FUNNEL_STAGE_COLUMN_OVERSCAN = 1;
const WHEEL_LINE_HEIGHT_PX = 16;
const FALLBACK_BOARD_WIDTH = 1024;

interface VirtualStageItem {
  stage: LeadsFunnelBoardStage;
  index: number;
  offsetLeft: number;
}

interface GetVisibleLeadsFunnelColumnsParams {
  availableWidth: number;
  maxVisibleColumns: number;
  stageCount: number;
  isMobile: boolean;
}

interface UseLeadsFunnelBoardColumnsParams {
  stages: LeadsFunnelBoardStage[];
  isDragActive: boolean;
}

interface UseLeadsFunnelBoardColumnsResult {
  containerRef: RefCallback<HTMLDivElement>;
  scrollRef: RefCallback<HTMLDivElement>;
  virtualStages: VirtualStageItem[];
  visibleCount: number;
  columnWidth: number;
  totalTrackWidth: number;
  skeletonVisibleCount: number;
  skeletonColumnWidth: number;
  skeletonTrackWidth: number;
  hasHorizontalOverflow: boolean;
  viewportWidth: number;
  scrollLeft: number;
  onHorizontalScroll(event: UIEvent<HTMLDivElement>): void;
  onScrollTo(nextScrollLeft: number): void;
}

export const getVisibleLeadsFunnelColumns = ({
  availableWidth,
  maxVisibleColumns,
  stageCount,
  isMobile,
}: GetVisibleLeadsFunnelColumnsParams): number => {
  if (stageCount <= 0) {
    return 0;
  }

  if (isMobile) {
    return 1;
  }

  const visibleByWidth = Math.max(
    1,
    Math.floor(
      (availableWidth + LEADS_FUNNEL_STAGE_COLUMN_GAP) /
        (MIN_LEADS_FUNNEL_STAGE_COLUMN_WIDTH + LEADS_FUNNEL_STAGE_COLUMN_GAP)
    )
  );

  return Math.min(maxVisibleColumns, visibleByWidth, stageCount);
};

const getColumnWidth = (
  availableWidth: number,
  visibleCount: number
): number => {
  if (visibleCount <= 0) {
    return availableWidth;
  }

  return (
    (availableWidth - LEADS_FUNNEL_STAGE_COLUMN_GAP * (visibleCount - 1)) /
    visibleCount
  );
};

const getTrackWidth = (itemCount: number, columnWidth: number): number => {
  if (itemCount <= 0) {
    return 0;
  }

  return (
    itemCount * columnWidth + LEADS_FUNNEL_STAGE_COLUMN_GAP * (itemCount - 1)
  );
};

export const useLeadsFunnelBoardColumns = ({
  stages,
  isDragActive,
}: UseLeadsFunnelBoardColumnsParams): UseLeadsFunnelBoardColumnsResult => {
  const { isMobile } = useWindowSize();
  const { maxVisibleColumns } = useLeadsRegistrySettings('funnels');

  const scrollElementRef = useRef<HTMLDivElement | null>(null);
  const frozenScrollLeftRef = useRef(0);
  const wasDragActiveRef = useRef(false);
  const prevFunnelIdRef = useRef<number | null>(null);
  const [containerNode, setContainerNode] = useState<HTMLDivElement | null>(
    null
  );
  const [boardWidth, setBoardWidth] = useState(FALLBACK_BOARD_WIDTH);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollNode, setScrollNode] = useState<HTMLDivElement | null>(null);
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    setContainerNode(node);
  }, []);
  const scrollRef = useCallback((node: HTMLDivElement | null) => {
    scrollElementRef.current = node;
    setScrollNode(node);
  }, []);

  useLayoutEffect(() => {
    if (!containerNode) {
      return undefined;
    }

    const updateBoardWidth = () => {
      setBoardWidth((prev) => {
        const next = containerNode.clientWidth;

        return prev === next || next === 0 ? prev : next;
      });
    };

    updateBoardWidth();

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(updateBoardWidth);
    resizeObserver.observe(containerNode);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerNode]);

  useLayoutEffect(() => {
    if (isDragActive && !wasDragActiveRef.current) {
      frozenScrollLeftRef.current = scrollLeft;
    }

    wasDragActiveRef.current = isDragActive;
  }, [isDragActive, scrollLeft]);

  const stageCount = stages.length;
  const funnelId = stages[0]?.funnelId ?? null;

  useLayoutEffect(() => {
    if (prevFunnelIdRef.current === funnelId) {
      return;
    }

    prevFunnelIdRef.current = funnelId;
    setScrollLeft(0);

    if (scrollElementRef.current) {
      scrollElementRef.current.scrollLeft = 0;
    }
  }, [funnelId]);

  useEffect(() => {
    if (!scrollNode) {
      return undefined;
    }

    const horizontalWheelHandler = (event: WheelEvent) => {
      if (!event.shiftKey || event.ctrlKey || event.metaKey) {
        return;
      }

      const maxScrollLeft = scrollNode.scrollWidth - scrollNode.clientWidth;

      if (maxScrollLeft <= 0) {
        return;
      }

      const wheelDelta =
        Math.abs(event.deltaX) > Math.abs(event.deltaY)
          ? event.deltaX
          : event.deltaY;

      if (wheelDelta === 0) {
        return;
      }

      const deltaMultiplier =
        event.deltaMode === WheelEvent.DOM_DELTA_LINE
          ? WHEEL_LINE_HEIGHT_PX
          : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
          ? scrollNode.clientWidth
          : 1;
      const nextScrollLeft = Math.min(
        maxScrollLeft,
        Math.max(0, scrollNode.scrollLeft + wheelDelta * deltaMultiplier)
      );

      event.preventDefault();

      if (nextScrollLeft === scrollNode.scrollLeft) {
        return;
      }

      scrollNode.scrollLeft = nextScrollLeft;
      setScrollLeft(nextScrollLeft);
    };

    scrollNode.addEventListener('wheel', horizontalWheelHandler, {
      capture: true,
      passive: false,
    });

    return () => {
      scrollNode.removeEventListener('wheel', horizontalWheelHandler, true);
    };
  }, [scrollNode]);

  const visibleCount = getVisibleLeadsFunnelColumns({
    availableWidth: boardWidth,
    maxVisibleColumns,
    stageCount,
    isMobile,
  });
  const columnWidth = getColumnWidth(boardWidth, visibleCount);
  const totalTrackWidth = getTrackWidth(stageCount, columnWidth);
  const maxScrollLeft = Math.max(0, totalTrackWidth - boardWidth);
  const hasHorizontalOverflow = stageCount > visibleCount;
  const columnStride = columnWidth + LEADS_FUNNEL_STAGE_COLUMN_GAP;
  const effectiveScrollLeft =
    isDragActive && wasDragActiveRef.current
      ? frozenScrollLeftRef.current
      : scrollLeft;
  const safeScrollLeft = Math.min(
    effectiveScrollLeft,
    Math.max(0, totalTrackWidth - boardWidth)
  );

  const virtualStages = useMemo(() => {
    if (stageCount === 0 || columnStride <= 0) {
      return [];
    }

    const firstVisibleIndex = Math.min(
      stageCount - 1,
      Math.max(0, Math.floor(safeScrollLeft / columnStride))
    );
    const visibleEndIndex = Math.min(
      stageCount,
      Math.max(
        firstVisibleIndex + 1,
        Math.ceil((safeScrollLeft + boardWidth) / columnStride)
      )
    );
    const startIndex = Math.max(
      0,
      firstVisibleIndex - LEADS_FUNNEL_STAGE_COLUMN_OVERSCAN
    );
    const endIndex = Math.min(
      stageCount,
      visibleEndIndex + LEADS_FUNNEL_STAGE_COLUMN_OVERSCAN
    );

    return stages.slice(startIndex, endIndex).map((stage, itemIndex) => {
      const index = startIndex + itemIndex;

      return {
        stage,
        index,
        offsetLeft: index * columnStride,
      };
    });
  }, [boardWidth, columnStride, safeScrollLeft, stageCount, stages]);

  const skeletonVisibleCount = getVisibleLeadsFunnelColumns({
    availableWidth: boardWidth,
    maxVisibleColumns,
    stageCount: maxVisibleColumns,
    isMobile,
  });
  const skeletonColumnWidth = getColumnWidth(boardWidth, skeletonVisibleCount);
  const skeletonTrackWidth = getTrackWidth(
    skeletonVisibleCount,
    skeletonColumnWidth
  );

  const horizontalScrollHandler = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      setScrollLeft(event.currentTarget.scrollLeft);
    },
    []
  );

  const scrollToHandler = useCallback((nextScrollLeft: number) => {
    if (scrollElementRef.current) {
      scrollElementRef.current.scrollLeft = nextScrollLeft;
    }

    setScrollLeft(nextScrollLeft);
  }, []);

  return {
    containerRef,
    scrollRef,
    virtualStages,
    visibleCount,
    columnWidth,
    totalTrackWidth,
    skeletonVisibleCount,
    skeletonColumnWidth,
    skeletonTrackWidth,
    hasHorizontalOverflow,
    viewportWidth: boardWidth,
    scrollLeft: Math.min(scrollLeft, maxScrollLeft),
    onHorizontalScroll: horizontalScrollHandler,
    onScrollTo: scrollToHandler,
  };
};
